import { useEffect, useRef } from 'react'

const KONAMI_SEQUENCE = [
  'arrowup', 'arrowup', 'arrowdown', 'arrowdown',
  'arrowleft', 'arrowright', 'arrowleft', 'arrowright', 'b', 'a',
] as const

// Temporary alternate unlock we can hand out to people, then remove later.
const TEMP_SEQUENCE = ['8', '8', '6', '6', '4', '4', 't', 't', 'u', 'u'] as const

const UNLOCK_SEQUENCES: readonly (readonly string[])[] = [KONAMI_SEQUENCE, TEMP_SEQUENCE]
const MAX_SEQUENCE_LENGTH = Math.max(...UNLOCK_SEQUENCES.map((seq) => seq.length))

/**
 * Fires `onUnlock` when the Konami code (↑↑↓↓←→←→ b a) or the temporary
 * alternate sequence (886644ttuu) is typed.
 * Keystrokes inside inputs/textareas/contentEditable are ignored.
 * Desktop-only by nature (requires a keyboard).
 */
export function useKonamiCode(onUnlock: () => void): void {
  const bufferRef = useRef<string[]>([])
  const onUnlockRef = useRef(onUnlock)
  onUnlockRef.current = onUnlock

  useEffect(() => {
    const handler = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null
      const tag = target?.tagName
      if (tag === 'INPUT' || tag === 'TEXTAREA' || target?.isContentEditable === true) {
        return
      }

      const next = [...bufferRef.current, event.key.toLowerCase()].slice(
        -MAX_SEQUENCE_LENGTH
      )
      bufferRef.current = next

      const unlocked = UNLOCK_SEQUENCES.some((sequence) => {
        const tail = next.slice(-sequence.length)
        return (
          tail.length === sequence.length &&
          tail.every((key, index) => key === sequence[index])
        )
      })

      if (unlocked) {
        bufferRef.current = []
        onUnlockRef.current()
      }
    }

    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [])
}
